import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Comparison strip — "this isn't chatgpt."
 * Asymmetric two-column comparison rows on a cream background with a dark accent panel.
 */
export default function WhyNotChatGPT() {
  const { t } = useLanguage();
  if (!t.vs_rows) return null;

  return (
    <section
      id="why-evern"
      className="bg-ev-cream py-24 md:py-32 px-6 md:px-10"
      data-testid="why-evern-section">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-[820px] mb-14 md:mb-20">
          <p className="font-body text-[10px] uppercase tracking-[0.4em] text-ev-ink/60 mb-6">{t.vs_label}</p>
          <h2
            className="font-display font-light text-ev-ink lowercase mb-6"
            style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4.5rem)', letterSpacing: '-0.03em' }}>
            {t.vs_h}
          </h2>
          {t.vs_sub && (
            <p className="font-body text-base md:text-lg text-ev-ink/70 leading-relaxed max-w-[560px]">{t.vs_sub}</p>
          )}
        </motion.div>

        {/* Column headers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="px-5 md:px-7">
            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-ev-ink/40 lowercase">
              {t.vs_lhs_label}
            </p>
          </div>
          <div className="px-5 md:px-7">
            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-ev-lime-dim flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-ev-lime-dim" />
              {t.vs_rhs_label}
            </p>
          </div>
        </div>

        {/* Rows */}
        <div className="space-y-3 md:space-y-4">
          {t.vs_rows.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
              data-testid={`vs-row-${i}`}>
              <div className="bg-white/60 rounded-[1.25rem] px-5 md:px-7 py-5 md:py-6 flex items-start gap-3">
                <span className="font-body text-base md:text-lg text-ev-ink/30 mt-0.5 select-none">—</span>
                <p className="font-body text-base md:text-lg text-ev-ink/60 leading-snug line-through decoration-ev-ink/15 decoration-1">
                  {row.them}
                </p>
              </div>
              <div className="bg-ev-ink rounded-[1.25rem] px-5 md:px-7 py-5 md:py-6 flex items-start gap-3 relative overflow-hidden">
                <span className="font-display text-xl md:text-2xl text-ev-lime mt-0 leading-none select-none">+</span>
                <p className="font-body text-base md:text-lg text-white leading-snug">{row.us}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
